import { defineComponent, h, onMounted, ref, VNode } from 'vue'
import { QIcon, QSelect } from 'quasar'
import * as emailValidator from 'email-validator'

import { validStr } from 'booksprout'

import { BsComponentRef, bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { ComponentState } from '../useBsComponent'
import { BsChip } from '../index'
import { quasarHandlers } from '../quasarHandlers'
import { emitModel } from '../emitModel'

/**
 * The comma-separated entry box — reviewer emails, codes, tags. A QSelect in chip mode with the paste
 * handling the app actually needs: a pasted CSV or a pasted column of lines becomes one chip per entry.
 *
 * `rollingChange` is why this component is the reason `doBeforeAutoSave` exists on the composable: text
 * typed but not yet turned into a chip is lost on blur, and blur is when auto-save runs, so the pending
 * text has to be committed first.
 *
 * Its validation rules write `componentState`/`errorMessage` directly rather than returning a message,
 * because QSelect rules run on the chip array while the error belongs to the component as a whole.
 */
/**
 * A `$refs` entry holding a BsChipBox. The two screens that reach into one call the `autoSave` *prop*
 * back on the child — see the note on `BsComponentRef` for why the component type will not do.
 */
export type BsChipBoxRef = BsComponentRef & {
  // eslint-disable-next-line @typescript-eslint/ban-types
  autoSave: Function
}

export default defineComponent({
  name: 'BsChipBox',

  // See BsInput: undeclared DOM-named events are bound twice, once natively. `pasteComplete` is this
  // component's own.
  emits: [ 'input', 'update:modelValue', 'pasteComplete' ],

  /**
   * Off, so the test hooks can be placed rather than routed.
   *
   * Quasar 2's QSelect sets `inheritAttrs: false` itself and sends whatever it is handed to
   * `.q-select__focus-target` — a separate, `opacity: 0` input that exists only to hold focus. Both of
   * this component's hooks ended up there: `data-cy` (so `cy.component('usDownloadCodes', 'cy')`
   * matched an input rather than the field, and `.find('.bs-chip')` found none of the chips beside it)
   * and `data-model` (so a spec typed into the focus target — the keydown handler here still made a
   * chip, but QSelect never emitted the `blur` that runs auto-save). Quasar 1 had no focus target and
   * neither problem. `stampTestHooks` puts each one where it was.
   */
  inheritAttrs: false,

  props: {
    ...bsComponentProps,
    /**
     * Declared as `string` although an array is accepted too: the render branches on `Array.isArray`
     * and calls `.split(',')` on the string path. The class declared it the same way.
     */
    value: {
      type: [String, Array] as unknown as () => string,
      default: void 0
    },
    returnArray: { type: Boolean, default: false },
    triggerChars: {
      type: Array as unknown as () => string[],
      default: () => [',']
    },
    emailInput: { type: Boolean, default: false },
    readonlyValues: {
      type: Array as unknown as () => string[],
      default: void 0
    },
    maxCharsPerEntry: { type: Number, default: void 0 },
    maxEntries: { type: Number, default: void 0 }
  },

  setup (props, { emit }) {
    /**
     * A real ref, not a string one.
     *
     * `ref: 'bsChipBox'` in a render function does not populate `$refs` for a component whose logic
     * lives in `setup()` under Vue 3, so `onMounted` found nothing and the paste handler -- the whole
     * point of reaching for the input -- was never attached. A ref object works from both places, and
     * the render passes it directly.
     */
    const bsChipBox = ref<QSelect | null>(null)
    const vm = useVm()
    /**
     * The two methods setup reaches back for. They are dispatched through the instance rather than called
     * as closures so that replacing one on the component — which the paste and blur paths rely on —
     * is honoured, exactly as the class's own dynamic dispatch was.
     */
    const host = vm as typeof vm & {
      setContent: (str: string) => void
      setCsvData: (csv: string, separator?: string) => void
    }

    const rollingChange = ref('')
    const invalidValues = ref<string[]>([])

    const bs = useBsComponent({
      vm,
      props,
      emit,
      value: () => props.value,
      // Every chip is a change; see the option's note in useBsComponent.
      autoSaveAlways: true,
      // Its rules mark it invalid where `validate()` cannot see; see that option's note too. [sc-20331]
      autoSaveOffWhileInvalid: true,
      doBeforeAutoSave: () => {
        if (validStr(rollingChange.value)) {
          host.setContent(rollingChange.value)
        }
      }
    })

    onMounted(() => {
      /**
       * Attach a paste event to the underlying input.
       *
       * Was `$refs.target` on the QSelect. Quasar 2 builds its inner input through a setup template
       * ref, which is not on `$refs` and is not exposed, so the input is found in the DOM instead.
       */
      // `input.q-field__input`, not just `input`: QSelect renders a hidden `.q-select__focus-target`
      // beside the real one, and the paste handler was going to whichever came first in the DOM.
      const input = bsChipBox.value?.$el
        ?.querySelector?.('input.q-field__input') as HTMLElement | null | undefined

      if (input === void 0 || input === null) {
        return
      }

      input.addEventListener('paste', (e: any) => {
        const pasteData = (e.clipboardData || (window as any).clipboardData).getData('text')

        // If it has commas, process it and add each individually.
        if (pasteData.indexOf(',') > -1) {
          host.setCsvData(pasteData)
        } else if (pasteData.indexOf('\n') > -1) { // If user copied data from upload file
          host.setCsvData(pasteData, '\n')
        } else {
          host.setContent(pasteData)
          vm.$emit('pasteComplete')
        }
      })
    })

    return {
      ...bs,
      bsChipBox,
      /**
       * A function ref for the render to pass.
       *
       * The render cannot see `bsChipBox` (it is in setup scope) and `this.bsChipBox` auto-unwraps to
       * the VALUE, which is null at render time -- so neither reaches the ref object. A function
       * returned from setup is not unwrapped, so this is what closes over it.
       */
      setChipBoxRef: (el: unknown) => { bsChipBox.value = el as QSelect | null },
      rollingChange,
      invalidValues
    }
  },

  mounted () {
    this.stampTestHooks()
  },

  updated () {
    this.stampTestHooks()
  },

  methods: {
    /**
     * `data-cy` on the field, `data-model` on the input a person types into. See `inheritAttrs`.
     *
     * `updated` as well as `mounted` because QSelect rebuilds its input as chips come and go, and
     * `.q-field__input` rather than `.q-field__native`: the latter is QSelect's display area, and only
     * a `use-input` select has the former at all.
     */
    stampTestHooks () {
      const root = (this.bsChipBox as { $el?: Element } | undefined)?.$el
      if (root === void 0 || root === null || root.setAttribute === void 0) {
        return
      }

      const dataCy = this.$attrs['data-cy']
      if (typeof dataCy === 'string') {
        root.setAttribute('data-cy', dataCy)
      }

      const input = root.querySelector?.('input.q-field__input')
      if (input !== void 0 && input !== null && this.dataModel !== void 0) {
        input.setAttribute('data-model', this.dataModel)
      }
    },

    setContent (str: string) {
      const qSelect = this.bsChipBox as QSelect
      qSelect.add(str.replace('\n', ''), true)

      this.rollingChange = ''
    },

    setCsvData (csv: string, separator = ',') {
      const
        pasteArray = csv.split(separator),
        items = pasteArray.map((m: any) => m.trim()).filter((f: any) => !!f)

      for (let i = 0; i < items.length; i++) {
        const data = items[i]

        if (data) {
          // Add doesn't quite work fast enough so need a timeout here.
          setTimeout(() => {
            this.setContent(data)

            if (i === items.length - 1) {
              this.$emit('pasteComplete')
            }
          }, 50)
        }
      }
    },

    /** Renamed from `__renderChip`: Vue does not proxy setup/method keys beginning with `_`. */
    renderChip (scope: any) {
      const isError = this.invalidValues.indexOf(scope.opt) > -1
      const isReadonly = this.readonlyValues?.includes(scope.opt)

      return h(BsChip, {
        class: ['text-bold', isReadonly ? 'bs-chip__readonly' : ''],
        dense: true,
        color:
            !isError
              ? isReadonly
                ? 'bs-lb-r'
                : 'bs-g'
              : 'bs-r',
        textColor: isReadonly && !isError ? 'bs-lb' : 'white'
      // BsChip is a component, so its children are a slot function -- and the `false` a readonly
      // chip produces has to be filtered out, where Vue 2 dropped it.
      }, { default: () => [
        h('span', {
          class: 'ellipsis'
        }, [
          scope.opt
        ]),
        (!isReadonly && h(QIcon, {
          class: 'cursor-pointer',
          name: 'app:close',
          size: '.5rem',
          onClick: () => scope.removeAtIndex(scope.index)
        })) as VNode | false
      ].filter(child => child !== false && child !== void 0) as VNode[] })
    }
  },

  render () {
    /**
     * `componentValue`, not `props.value`: a caller writing `v-model` sends `modelValue`, so the
     * declared `value` prop is undefined for every one of them and this resolved to null on every
     * render. The chips then came only from QSelect's own internal list, which is rebuilt from the
     * model each time — so adding a second code threw the first away, and a pasted column of nine or a
     * CSV of twenty-five arrived as exactly one chip. (BsDate carries the same note for the same
     * reason.)
     */
    const current = this.componentValue as string | string[] | undefined

    const value = typeof current === 'string' && current.indexOf(',') > -1
      // CSV data
      ? current.split(',').map((m: any) => m.trim()).filter((f: any) => !!f)
      : (
        // Is an array
        Array.isArray(current)
          ? current
          // Not an array, make one.
          : current ? [current] : null
      )

    const props = {
      filled: true,
      useInput: true,
      useChips: true,
      multiple: true,
      hideDropdownIcon: true,
      inputDebounce: '0',
      newValueMode: 'add-unique',
      bottomSlots: true,
      value,
      rules: [
        (chipValues: string[]) => {
          if (!chipValues || chipValues.length === 0) return true

          let foundInvalid = false
          for (const val of chipValues) {
            if (val === '' || emailValidator.validate(val) || !this.emailInput) {
              if (!foundInvalid) {
                this.componentState = ComponentState.Valid
              }
              continue
            }

            this.invalidValues.push(val)
            this.componentState = ComponentState.Invalid
            this.errorMessage = this.$tc('system.errors.chipBox.invalidEmailsDetected')
            foundInvalid = true
          }

          return this.componentState === ComponentState.Valid
        },
        (chipValues: string[]) => {
          if (!chipValues || chipValues.length === 0) return true

          for (const val of chipValues) {
            if (this.maxCharsPerEntry !== void 0 && val && val.length > this.maxCharsPerEntry) {
              this.invalidValues.push(val)
              this.componentState = ComponentState.Invalid
              this.errorMessage = this.$tc('system.errors.chipBox.maxCharsPerEntry', void 0, {
                userEnteredValue: val,
                maxCharsPerEntry: this.maxCharsPerEntry
              })

              return false
            }
          }

          return this.componentState === ComponentState.Valid
        }
      ]
    }

    const listeners = {
      keydown: (e: any) => {
        // If the user presses the tab key and they've entered some text, add it as a chip.
        if (
          (
            // legacy -> https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
            e.keyCode === 9 /*TAB*/ ||
            e.keyCode === 13 /*ENTER*/ ||
            e.which === 9 /*TAB*/ ||
            e.which === 13 /*ENTER*/ ||
            e.key === 'Tab' ||
            e.key === 'Enter' ||
            // newest property, as per suggestion by MDN (see above link)
            e.code === 'Tab' ||
            e.code === 'Enter'
          ) && validStr(e.target.value)) {
          if (e.target.value.indexOf(',') > -1) {
            this.setCsvData(e.target.value)
          } else {
            this.setContent(e.target.value)
          }
        }
      },
      keyup: (e: any) => {
        // This is to track the data inputted for when we use doOnAutosave (blur) so we can save the value before it's lost.
        this.rollingChange = e.target.value

        if (this.triggerChars.includes(e.key)) {
          let value = e.target.value
          this.triggerChars.forEach(c => value = value.replace(c, ''))
          this.setContent(value)
        }
      },
      input: (items: []) => {
        const concatenatedArray = items.reduce((accumulator: string[], currentValue: string) => {
          if (currentValue && currentValue.includes(',')) {
            return [...accumulator, ...currentValue.split(',')]
          }

          return [...accumulator, currentValue]
        }, [])
        const uniqueConcatenatedArray = [...new Set(concatenatedArray)]
        const returnData = this.returnArray ? uniqueConcatenatedArray : uniqueConcatenatedArray.join(',')
        const qSelect = this.bsChipBox as QSelect

        if (this.maxEntries !== void 0 && uniqueConcatenatedArray.length > this.maxEntries) return

        emitModel(this, returnData)

        // clear input value once validated and data is emitted
        qSelect.updateInputValue('', true)
      }
    }

    return h(QSelect, {
      class: ['bs-chip-box', this.getClasses()],
      ref: this.setChipBoxRef,
      ...this.getRenderProps(props),
      ...quasarHandlers(this.getListeners(listeners)),
      // `true`: `stampTestHooks` places `data-model` itself — see `inheritAttrs` above.
      ...this.getAttributes(void 0, true),
      /**
       * LAST, so the chip list wins. `getRenderProps` re-spreads `$attrs`, and a caller's `v-model`
       * puts the raw comma string in there under `modelValue` — which reached QSelect as one option
       * rather than the split list. Same ordering rule, and same reason, as BsDate's.
       */
      modelValue: value
    }, {
      ...this.getSlots({
        ['selected-item']: (slotData: any) => {
          return this.renderChip(slotData)
        }
      }),
      // Vue 3 takes ONE slots object; what this passed as a fourth argument is the default slot.
      default: () => [
        this.slot(this, 'default'),
        this.slot(this, 'hint')
      ]
    })
  }
})
